import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { Breadcrumb } from '../components/Breadcrumb';
import { DateRangeFilter } from '../components/Agenda/DateRangeFilter';
import { MetricsProcessStatusFilter } from '../components/Metrics/MetricsStatusFilter';
import { MetricsPartyTypeFilter } from '../components/Metrics/MetricsPartyTypeFilter';
import { MetricsTagFilter } from '../components/Metrics/MetricsTagFilter';
import { ThroughputChart } from '../components/Metrics/ThroughputChart';
import { DistributionChart } from '../components/Metrics/DistributionChart';
import { OverdueByMemberChart } from '../components/Metrics/OverdueByMemberChart';
import { ProveitoChart } from '../components/Metrics/ProveitoChart';
import { usePermissions } from '../hooks/usePermissions';
import { useWorkspaceStore } from '../stores/workspace.store';
import type {
  WorkspaceMetricsDto,
  ClientMetricsDto,
  MemberMetricsDto,
  ArchivedSnapshotsDto,
  MetricsPeriodPreset,
  MetricsStatusFilter,
  MetricsPartyType,
} from '@prometeus/contracts';
import type { ClientDto } from '@prometeus/contracts';
import type { TagDto } from '@prometeus/contracts';

export const Route = createFileRoute('/w/$workspaceId/metrics')({
  component: MetricsPage,
});

type MetricsMode = 'workspace' | 'client' | 'member';

type RoleRow = { role: 'autor' | 'reu'; caseValue: number; condemnationValue: number; economicBenefitValue: number };
function pivotByRole<T extends RoleRow>(
  rows: T[],
  getKey: (r: T) => string,
  getLabel: (r: T) => string
): { label: string; autor: RoleRow; reu: RoleRow }[] {
  const map = new Map<string, { label: string; autor: RoleRow; reu: RoleRow }>();
  const empty = (): RoleRow => ({ role: 'autor', caseValue: 0, condemnationValue: 0, economicBenefitValue: 0 });
  for (const r of rows) {
    const k = getKey(r);
    if (!map.has(k))
      map.set(k, { label: getLabel(r), autor: empty(), reu: empty() });
    const e = map.get(k)!;
    const v: RoleRow = { role: r.role, caseValue: r.caseValue, condemnationValue: r.condemnationValue, economicBenefitValue: r.economicBenefitValue };
    if (r.role === 'autor') e.autor = v;
    else e.reu = v;
  }
  return Array.from(map.values());
}

const cardStyle = { borderRadius: '15px', boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)' };

function ProveitoBlock({ data }: { data: ArchivedSnapshotsDto }) {
  const byTagRows = pivotByRole(data.byTag ?? [], (r) => r.tagId ?? r.tagName ?? '—', (r) => r.tagName ?? '—');
  const dimTables = [
    { title: 'Por tag', rows: byTagRows },
    { title: 'Por órgão', rows: pivotByRole(data.byCourtOrg ?? [], (r) => r.dimensionLabel, (r) => r.dimensionLabel) },
    { title: 'Por tribunal', rows: pivotByRole(data.byCourt ?? [], (r) => r.dimensionLabel, (r) => r.dimensionLabel) },
    { title: 'Por classe judicial', rows: pivotByRole(data.byJudicialClass ?? [], (r) => r.dimensionLabel, (r) => r.dimensionLabel) },
    { title: 'Por prioridade', rows: pivotByRole(data.byPriority ?? [], (r) => r.dimensionLabel, (r) => r.dimensionLabel) },
  ];
  return (
    <div className="bg-gray-50 dark:bg-gray-700 p-6 overflow-x-auto space-y-6 rounded-[15px]" style={cardStyle}>
      <h3 className="text-sm font-semibold text-[#2D3748] dark:text-gray-200">Proveito econômico (ao arquivar)</h3>
      <p className="text-xs text-[#718096] dark:text-gray-400">
        Autor — valor da causa · condenação · proveito econômico · Réu — valor da causa · condenação · proveito econômico
      </p>
      {data.total.length > 0 && (
        <div className="mb-4">
          <ProveitoChart total={data.total} valueKey="economicBenefitValue" />
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-600">
            <th className="text-left py-2">Polo</th>
            <th className="text-right py-2">Valor causa</th>
            <th className="text-right py-2">Condenação</th>
            <th className="text-right py-2">Proveito econômico</th>
            <th className="text-right py-2">Processos</th>
          </tr>
        </thead>
        <tbody>
          {(['autor', 'reu'] as const).map((role) => {
            const t = data.total.find((x) => x.role === role);
            return (
              <tr key={role} className="border-b border-gray-100 dark:border-gray-700">
                <td className="py-2 font-medium">{role === 'autor' ? 'Autor (Ativo)' : 'Réu (Passivo)'}</td>
                <td className="text-right py-2">{t?.caseValue ?? 0}</td>
                <td className="text-right py-2">{t?.condemnationValue ?? 0}</td>
                <td className="text-right py-2">{t?.economicBenefitValue ?? 0}</td>
                <td className="text-right py-2">{t?.processCount ?? 0}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {dimTables.map(
        (s) =>
          s.rows.length > 0 && (
            <div key={s.title}>
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">{s.title}</h4>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-600">
                    <th className="text-left py-2">Valor</th>
                    <th className="text-right py-2">Autor valor causa</th>
                    <th className="text-right py-2">Autor condenação</th>
                    <th className="text-right py-2">Autor proveito</th>
                    <th className="text-right py-2">Réu valor causa</th>
                    <th className="text-right py-2">Réu condenação</th>
                    <th className="text-right py-2">Réu proveito</th>
                  </tr>
                </thead>
                <tbody>
                  {s.rows.map((row, i) => (
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-700">
                      <td className="py-2">{row.label}</td>
                      <td className="text-right py-2">{row.autor.caseValue}</td>
                      <td className="text-right py-2">{row.autor.condemnationValue}</td>
                      <td className="text-right py-2">{row.autor.economicBenefitValue}</td>
                      <td className="text-right py-2">{row.reu.caseValue}</td>
                      <td className="text-right py-2">{row.reu.condemnationValue}</td>
                      <td className="text-right py-2">{row.reu.economicBenefitValue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      )}
    </div>
  );
}

function MetricsPage() {
  const { workspaceId } = Route.useParams();
  const { hasPermission } = usePermissions();
  const setWorkspace = useWorkspaceStore((s) => s.setWorkspace);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);

  const [mode, setMode] = useState<MetricsMode>('workspace');
  const [period, setPeriod] = useState<MetricsPeriodPreset>('30d');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [status, setStatus] = useState<MetricsStatusFilter>('both');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [partyType, setPartyType] = useState<MetricsPartyType | ''>('');
  const [clientId, setClientId] = useState('');
  const [memberId, setMemberId] = useState('');

  useEffect(() => {
    if (!workspaceId) return;
    localStorage.setItem('workspaceId', workspaceId);
    if (!currentWorkspace || currentWorkspace.id !== workspaceId) {
      setWorkspace({ id: workspaceId, name: '', slug: '' });
    }
  }, [workspaceId, currentWorkspace, setWorkspace]);

  const { data: workspace } = useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: async () => {
      const r = await api.get(`/workspaces/${workspaceId}`);
      return r.data.data;
    },
    enabled: !!workspaceId,
  });

  const { data: clients = [] } = useQuery<ClientDto[]>({
    queryKey: ['clients', workspaceId],
    queryFn: async () => {
      const r = await api.get('/clients');
      return Array.isArray(r.data.data) ? r.data.data : r.data.data?.data ?? [];
    },
    enabled: !!workspaceId && hasPermission('clients.read'),
  });

  const { data: members = [] } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: async () => {
      const r = await api.get('/members');
      return r.data.data ?? [];
    },
    enabled: !!workspaceId && hasPermission('members.read'),
  });

  const { data: tags = [] } = useQuery<TagDto[]>({
    queryKey: ['tags', workspaceId],
    queryFn: async () => {
      const r = await api.get('/tags');
      return r.data.data ?? [];
    },
    enabled: !!workspaceId && hasPermission('tags.read'),
  });

  const queryParams = useMemo(() => {
    const p: Record<string, string> = { period };
    if (period === 'custom' && dateStart) p.date_start = dateStart;
    if (period === 'custom' && dateEnd) p.date_end = dateEnd;
    if (status && status !== 'both') p.status = status;
    if (tagIds.length) p.tag_ids = tagIds.join(',');
    if (partyType) p.party_type = partyType;
    if (mode === 'workspace' && clientId) p.client_id = clientId;
    return p;
  }, [period, dateStart, dateEnd, status, tagIds, partyType, mode, clientId]);

  const archivedSnapshotsParams = useMemo(() => {
    const p: Record<string, string> = { period };
    if (period === 'custom' && dateStart) p.date_start = dateStart;
    if (period === 'custom' && dateEnd) p.date_end = dateEnd;
    if (tagIds.length) p.tag_ids = tagIds.join(',');
    if (partyType) p.party_type = partyType;
    if (mode === 'client' && clientId) p.client_id = clientId;
    return p;
  }, [period, dateStart, dateEnd, tagIds, partyType, mode, clientId]);

  const { data: wsMetrics, isLoading: loadingWs } = useQuery<WorkspaceMetricsDto>({
    queryKey: ['metrics', 'workspace', workspaceId, queryParams],
    queryFn: async () => {
      const r = await api.get('/metrics/workspace', { params: queryParams });
      return r.data.data ?? r.data;
    },
    enabled: !!workspaceId && hasPermission('metrics.view') && mode === 'workspace',
  });

  const { data: clientMetrics, isLoading: loadingClient } = useQuery<ClientMetricsDto>({
    queryKey: ['metrics', 'client', workspaceId, clientId, queryParams],
    queryFn: async () => {
      const r = await api.get(`/metrics/client/${clientId}`, { params: queryParams });
      return r.data.data ?? r.data;
    },
    enabled: !!workspaceId && !!clientId && hasPermission('metrics.view') && mode === 'client',
  });

  const { data: memberMetrics, isLoading: loadingMember } = useQuery<MemberMetricsDto>({
    queryKey: ['metrics', 'member', workspaceId, memberId, queryParams],
    queryFn: async () => {
      const r = await api.get(`/metrics/member/${memberId}`, { params: queryParams });
      return r.data.data ?? r.data;
    },
    enabled: !!workspaceId && !!memberId && hasPermission('metrics.view') && mode === 'member',
  });

  const { data: archivedSnapshots } = useQuery<ArchivedSnapshotsDto>({
    queryKey: ['metrics', 'archived-snapshots', workspaceId, archivedSnapshotsParams],
    queryFn: async () => {
      const r = await api.get('/metrics/archived-snapshots', { params: archivedSnapshotsParams });
      return r.data.data ?? r.data;
    },
    enabled:
      !!workspaceId &&
      hasPermission('metrics.view') &&
      (mode === 'workspace' || (mode === 'client' && !!clientId)),
  });

  if (!hasPermission('metrics.view')) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Breadcrumb
            items={[
              { label: workspace?.name || 'Espaço de trabalho', to: '/w/$workspaceId/clients', params: { workspaceId } },
              { label: 'Métricas' },
            ]}
          />
          <div className="bg-white dark:bg-gray-800 p-6 mt-6" style={{ borderRadius: '15px', boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)' }}>
            <p className="text-center text-sm text-red-500 dark:text-red-400">
              Você não tem permissão para acessar Métricas.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const subtitleByMode =
    mode === 'workspace'
      ? 'Indicadores gerais do espaço de trabalho'
      : mode === 'client'
        ? 'Métricas por cliente'
        : 'Métricas por membro';

  const loading = mode === 'workspace' ? loadingWs : mode === 'client' ? loadingClient : loadingMember;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <div className="max-w-7xl mx-auto px-6 py-6">
        <Breadcrumb
          items={[
            { label: workspace?.name || 'Espaço de trabalho', to: '/w/$workspaceId/clients', params: { workspaceId } },
            { label: 'Métricas' },
          ]}
        />

        <div className="bg-white dark:bg-gray-800 mt-6 p-6" style={{ borderRadius: '15px', boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)' }}>
          <div className="mb-6">
            <h1 className="text-[18px] font-semibold text-[#2D3748] dark:text-gray-100 mb-1">Métricas</h1>
            <p className="text-[12px] text-[#718096] dark:text-gray-400">{subtitleByMode}</p>
          </div>

          <div className="border-b border-gray-200 dark:border-gray-600 mb-6">
            <nav className="-mb-px flex space-x-8">
              {(['workspace', 'client', 'member'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`py-4 px-1 border-b-2 transition-colors ${
                    mode === m
                      ? 'border-[#485A6A] text-[#485A6A] dark:text-[#485A6A]'
                      : 'border-transparent hover:border-gray-300 dark:hover:border-gray-500 text-[#718096] dark:text-gray-400'
                  }`}
                  style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', fontWeight: 500 }}
                >
                  {m === 'workspace' ? 'Visão geral' : m === 'client' ? 'Por cliente' : 'Por membro'}
                </button>
              ))}
            </nav>
          </div>

          <div className="flex flex-wrap gap-3 items-center mb-6">
          <span className="text-[11px] font-semibold text-[#2D3748] dark:text-gray-300">Período</span>
          {(['today', '7d', '30d', '90d', 'custom'] as const).map((p) => (
            <button
              key={p}
              onClick={() => {
                setPeriod(p);
                if (p !== 'custom') setDateStart('');
                if (p !== 'custom') setDateEnd('');
              }}
              className={`px-3 py-2 rounded-[10px] text-[12px] font-medium transition-colors border focus:outline-none focus:ring-2 focus:ring-[#485A6A] focus:border-[#485A6A] ${
                period === p
                  ? 'bg-[#485A6A] text-white border-[#485A6A]'
                  : 'bg-white dark:bg-gray-800 text-[#2D3748] dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {p === 'today'
                ? 'Hoje'
                : p === '7d'
                  ? '7 dias'
                  : p === '30d'
                    ? '30 dias'
                    : p === '90d'
                      ? '90 dias'
                      : 'Personalizado'}
            </button>
          ))}
          {period === 'custom' && (
            <DateRangeFilter
              dateFrom={dateStart}
              dateTo={dateEnd}
              onChange={(from, to) => {
                setDateStart(from);
                setDateEnd(to);
              }}
              onClear={() => {
                setDateStart('');
                setDateEnd('');
              }}
            />
          )}
          <MetricsProcessStatusFilter value={status} onChange={setStatus} />
          <MetricsPartyTypeFilter value={partyType} onChange={setPartyType} />
          <MetricsTagFilter tags={tags} selectedIds={tagIds} onChange={setTagIds} />
          {mode === 'client' && (
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="min-w-[200px] px-4 py-2 rounded-[10px] border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-[#2D3748] dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#485A6A] focus:border-[#485A6A]"
              style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px' }}
            >
              <option value="">Selecione um cliente</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
          {mode === 'member' && (
            <select
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              className="min-w-[200px] px-4 py-2 rounded-[10px] border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-[#2D3748] dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#485A6A] focus:border-[#485A6A]"
              style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px' }}
            >
              <option value="">Selecione um membro</option>
              {members.map((m: { id: string; user?: { name: string } }) => (
                <option key={m.id} value={m.id}>{m.user?.name ?? m.id}</option>
              ))}
            </select>
          )}
        </div>

        {loading && (
          <div className="mt-8 text-center text-gray-500 dark:text-gray-400 text-sm">Carregando...</div>
        )}

        {!loading && mode === 'workspace' && wsMetrics && (
          <div className="mt-8 space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
              {[
                ['Processos (total)', wsMetrics.cards.processesTotal],
                ['Processos ativos', wsMetrics.cards.processesActive],
                ['Processos arquivados', wsMetrics.cards.processesArchived],
                ['Clientes', wsMetrics.cards.clientsTotal],
                ['Membros', wsMetrics.cards.membersTotal],
                ['Tarefas abertas', wsMetrics.cards.tasksOpen],
                ['Tarefas realizadas', wsMetrics.cards.tasksDone],
                ['Tarefas atrasadas', wsMetrics.cards.tasksOverdue],
                ['Prazos abertos', wsMetrics.cards.deadlinesOpen],
                ['Prazos cumpridos', wsMetrics.cards.deadlinesDone],
                ['Prazos atrasados', wsMetrics.cards.deadlinesOverdue],
                ['Audiências abertas', wsMetrics.cards.hearingsOpen],
                ['Audiências realizadas', wsMetrics.cards.hearingsDone],
                ['Eventos abertos', wsMetrics.cards.eventsOpen],
                ['Eventos realizados', wsMetrics.cards.eventsDone],
                ['Publicações (período)', wsMetrics.cards.publicationsInPeriod],
                ['Publicações (total)', wsMetrics.cards.publicationsTotal],
                ['Valor de condenação (Ativo)', wsMetrics.cards.condemnationValueAutor],
                ['Valor de condenação (Passivo)', wsMetrics.cards.condemnationValueReu],
                ['Valor do proveito econômico (Ativo)', wsMetrics.cards.economicBenefitValueAutor],
                ['Valor do proveito econômico (Passivo)', wsMetrics.cards.economicBenefitValueReu],
                ['Itens pendentes (acumulado)', wsMetrics.backlog],
              ].map(([label, value]) => (
                <div key={String(label)} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-[15px]" style={cardStyle}>
                  <p className="text-[11px] font-semibold text-[#2D3748] dark:text-gray-300 uppercase mb-1">{label}</p>
                  <p className="text-lg font-semibold text-[#2D3748] dark:text-gray-100">{String(value)}</p>
                </div>
              ))}
            </div>
            {wsMetrics.leadTimeDays != null && (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-[15px]" style={cardStyle}>
                <p className="text-[11px] font-semibold text-[#2D3748] dark:text-gray-300 uppercase mb-1">Tempo médio até conclusão (dias)</p>
                <p className="text-lg font-semibold text-[#2D3748] dark:text-gray-100">{wsMetrics.leadTimeDays}</p>
              </div>
            )}
            {wsMetrics.throughput.length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-[15px] overflow-x-auto" style={cardStyle}>
                <h3 className="text-sm font-semibold text-[#2D3748] dark:text-gray-200 mb-4">Concluídos por dia (tarefas e prazos)</h3>
                <ThroughputChart data={wsMetrics.throughput} maxPoints={14} />
              </div>
            )}
            {wsMetrics.processesByTag.length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-[15px] overflow-x-auto" style={cardStyle}>
                <h3 className="text-sm font-semibold text-[#2D3748] dark:text-gray-200 mb-4">Processos por tag</h3>
                <div className="mb-4">
                  <DistributionChart
                    data={wsMetrics.processesByTag.map((r) => ({ label: r.tagName, count: r.count, autor: r.autor, reu: r.reu }))}
                    variant="bar"
                    maxItems={10}
                  />
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-600">
                      <th className="text-left py-2">Tag</th>
                      <th className="text-right py-2">Total</th>
                      <th className="text-right py-2">Autor</th>
                      <th className="text-right py-2">Réu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wsMetrics.processesByTag.map((r) => (
                      <tr key={r.tagId} className="border-b border-gray-100 dark:border-gray-700">
                        <td className="py-2">{r.tagName}</td>
                        <td className="text-right py-2">{r.count}</td>
                        <td className="text-right py-2">{r.autor}</td>
                        <td className="text-right py-2">{r.reu}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {[
              { title: 'Por fase', data: wsMetrics.byPhase },
              { title: 'Por classe judicial', data: wsMetrics.byJudicialClass },
              { title: 'Por comarca/foro', data: wsMetrics.byJurisdiction },
              { title: 'Por órgão', data: wsMetrics.byCourtOrg },
              { title: 'Por tribunal', data: wsMetrics.byCourt },
              { title: 'Por status', data: wsMetrics.byStatus },
              { title: 'Por prioridade/urgência', data: wsMetrics.byPriority },
            ].map(
              (s) =>
                s.data.length > 0 && (
                  <div key={s.title} className="bg-gray-50 dark:bg-gray-700 p-6 rounded-[15px] overflow-x-auto" style={cardStyle}>
                    <h3 className="text-sm font-semibold text-[#2D3748] dark:text-gray-200 mb-4">{s.title}</h3>
                    <div className="mb-4">
                      <DistributionChart
                        data={s.data.map((r) => ({ label: r.label, count: r.count, autor: r.countAutor, reu: r.countReu }))}
                        variant="bar"
                        maxItems={10}
                      />
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-600">
                          <th className="text-left py-2">Valor</th>
                          <th className="text-right py-2">Qtde</th>
                          <th className="text-right py-2">Ativo</th>
                          <th className="text-right py-2">Passivo</th>
                          <th className="text-right py-2">Valor causa Autor (Ativo)</th>
                          <th className="text-right py-2">Valor causa Réu (Passivo)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.data.map((r, i) => (
                          <tr key={r.key ?? i} className="border-b border-gray-100 dark:border-gray-700">
                            <td className="py-2">{r.label}</td>
                            <td className="text-right py-2">{r.count}</td>
                            <td className="text-right py-2">{r.countAutor}</td>
                            <td className="text-right py-2">{r.countReu}</td>
                            <td className="text-right py-2">{r.caseValueAutor}</td>
                            <td className="text-right py-2">{r.caseValueReu}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
            )}
            {wsMetrics.overdueByMember.length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-[15px] overflow-x-auto" style={cardStyle}>
                <h3 className="text-sm font-semibold text-[#2D3748] dark:text-gray-200 mb-4">Itens em atraso por responsável (top 10)</h3>
                <div className="mb-4">
                  <OverdueByMemberChart data={wsMetrics.overdueByMember} maxItems={10} />
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-600">
                      <th className="text-left py-2">Membro</th>
                      <th className="text-right py-2">Qtd.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wsMetrics.overdueByMember.map((r) => (
                      <tr key={r.memberId} className="border-b border-gray-100 dark:border-gray-700">
                        <td className="py-2">{r.memberName}</td>
                        <td className="text-right py-2">{r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {archivedSnapshots && <ProveitoBlock data={archivedSnapshots} />}
          </div>
        )}

        {!loading && mode === 'client' && clientId && clientMetrics && (
          <div className="mt-8 space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                ['Processos total', clientMetrics.cards.processesTotal],
                ['Como Autor', clientMetrics.cards.processesAsAutor],
                ['Como Réu', clientMetrics.cards.processesAsReu],
                ['Valor causa Autor', clientMetrics.cards.caseValueAutor],
                ['Valor causa Réu', clientMetrics.cards.caseValueReu],
                ['Valor de condenação (Ativo)', clientMetrics.cards.condemnationValueAutor],
                ['Valor de condenação (Passivo)', clientMetrics.cards.condemnationValueReu],
                ['Valor do proveito econômico (Ativo)', clientMetrics.cards.economicBenefitValueAutor],
                ['Valor do proveito econômico (Passivo)', clientMetrics.cards.economicBenefitValueReu],
                ['Tarefas realizadas', clientMetrics.tasksDone],
                ['Horas em execução', clientMetrics.cards.tasksTimeInProgressHours],
                ['Horas bloqueadas', clientMetrics.cards.tasksTimeBlockedHours],
                ['Horas suspensas', clientMetrics.cards.tasksTimeOnHoldHours],
                ['Prazos cumpridos', clientMetrics.deadlinesDone],
                ['Audiências realizadas', clientMetrics.hearingsDone],
                ['Eventos realizados', clientMetrics.eventsDone],
                ['Publicações (período)', clientMetrics.publicationsInPeriod],
              ].map(([label, value]) => (
                <div key={String(label)} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-[15px]" style={cardStyle}>
                  <p className="text-[11px] font-semibold text-[#2D3748] dark:text-gray-300 uppercase mb-1">{label}</p>
                  <p className="text-lg font-semibold text-[#2D3748] dark:text-gray-100">{String(value)}</p>
                </div>
              ))}
            </div>
            {(() => {
              const hoursInProgress = clientMetrics.cards.tasksTimeInProgressHours ?? 0;
              const hoursBlocked = clientMetrics.cards.tasksTimeBlockedHours ?? 0;
              const hoursOnHold = clientMetrics.cards.tasksTimeOnHoldHours ?? 0;
              const pieData = [
                { label: 'Em execução', count: hoursInProgress },
                { label: 'Bloqueadas', count: hoursBlocked },
                { label: 'Suspensas', count: hoursOnHold },
              ].filter((r) => (r.count ?? 0) > 0);

              return (
                <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-[15px] overflow-x-auto" style={cardStyle}>
                  <h3 className="text-sm font-semibold text-[#2D3748] dark:text-gray-200 mb-2">Horas por status (tarefas)</h3>
                  {pieData.length > 0 ? (
                    <>
                      <div className="mb-4">
                        <DistributionChart data={pieData} variant="pie" maxItems={3} />
                      </div>
                      <div className="text-[12px] text-[#2D3748] dark:text-gray-200 flex flex-col gap-1">
                        <div>Em execução: {hoursInProgress}h</div>
                        <div>Bloqueadas: {hoursBlocked}h</div>
                        <div>Suspensas: {hoursOnHold}h</div>
                      </div>
                    </>
                  ) : (
                    <div className="text-[12px] text-[#718096] dark:text-gray-400">Sem horas registradas no período.</div>
                  )}
                </div>
              );
            })()}
            {clientMetrics.hoursByProcess.length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-[15px] overflow-x-auto" style={cardStyle}>
                <h3 className="text-sm font-semibold text-[#2D3748] dark:text-gray-200 mb-4">
                  Horas por processo (top 10, em horas)
                </h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-600">
                      <th className="text-left py-2">Processo</th>
                      <th className="text-right py-2">Execução (h)</th>
                      <th className="text-right py-2">Bloqueadas (h)</th>
                      <th className="text-right py-2">Suspensas (h)</th>
                      <th className="text-right py-2">Total (h)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientMetrics.hoursByProcess.map((r) => (
                      <tr key={r.processId} className="border-b border-gray-100 dark:border-gray-700">
                        <td className="py-2">{r.processNumber}</td>
                        <td className="text-right py-2">{r.hoursInProgress}</td>
                        <td className="text-right py-2">{r.hoursBlocked}</td>
                        <td className="text-right py-2">{r.hoursOnHold}</td>
                        <td className="text-right py-2">{r.totalHours}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {clientMetrics.hoursByTag.length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-[15px] overflow-x-auto" style={cardStyle}>
                <h3 className="text-sm font-semibold text-[#2D3748] dark:text-gray-200 mb-4">
                  Horas por tag (top 10, em horas)
                </h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-600">
                      <th className="text-left py-2">Tag</th>
                      <th className="text-right py-2">Execução (h)</th>
                      <th className="text-right py-2">Bloqueadas (h)</th>
                      <th className="text-right py-2">Suspensas (h)</th>
                      <th className="text-right py-2">Total (h)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientMetrics.hoursByTag.map((r) => (
                      <tr key={r.tagId} className="border-b border-gray-100 dark:border-gray-700">
                        <td className="py-2">{r.tagName}</td>
                        <td className="text-right py-2">{r.hoursInProgress}</td>
                        <td className="text-right py-2">{r.hoursBlocked}</td>
                        <td className="text-right py-2">{r.hoursOnHold}</td>
                        <td className="text-right py-2">{r.totalHours}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {clientMetrics.byTag.length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-[15px] overflow-x-auto" style={cardStyle}>
                <h3 className="text-sm font-semibold text-[#2D3748] dark:text-gray-200 mb-4">Processos por tag</h3>
                <div className="mb-4">
                  <DistributionChart
                    data={clientMetrics.byTag.map((r) => ({
                      label: r.tagName,
                      count: r.countAutor + r.countReu,
                      autor: r.countAutor,
                      reu: r.countReu,
                    }))}
                    variant="bar"
                    maxItems={10}
                  />
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-600">
                      <th className="text-left py-2">Tag</th>
                      <th className="text-right py-2">Qtde Autor</th>
                      <th className="text-right py-2">Qtde Réu</th>
                      <th className="text-right py-2">Valor Autor</th>
                      <th className="text-right py-2">Valor Réu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientMetrics.byTag.map((r) => (
                      <tr key={r.tagId} className="border-b border-gray-100 dark:border-gray-700">
                        <td className="py-2">{r.tagName}</td>
                        <td className="text-right py-2">{r.countAutor}</td>
                        <td className="text-right py-2">{r.countReu}</td>
                        <td className="text-right py-2">{r.caseValueAutor}</td>
                        <td className="text-right py-2">{r.caseValueReu}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {[
              { title: 'Por fase', data: clientMetrics.byPhase },
              { title: 'Por classe judicial', data: clientMetrics.byJudicialClass },
              { title: 'Por comarca/foro', data: clientMetrics.byJurisdiction },
              { title: 'Por órgão', data: clientMetrics.byCourtOrg },
              { title: 'Por tribunal', data: clientMetrics.byCourt },
              { title: 'Por status', data: clientMetrics.byStatus },
              { title: 'Por prioridade/urgência', data: clientMetrics.byPriority },
            ].map(
              (s) =>
                s.data.length > 0 && (
                  <div key={s.title} className="bg-gray-50 dark:bg-gray-700 p-6 rounded-[15px] overflow-x-auto" style={cardStyle}>
                    <h3 className="text-sm font-semibold text-[#2D3748] dark:text-gray-200 mb-4">{s.title}</h3>
                    <div className="mb-4">
                      <DistributionChart
                        data={s.data.map((r) => ({ label: r.label, count: r.count, autor: r.countAutor, reu: r.countReu }))}
                        variant="bar"
                        maxItems={10}
                      />
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-600">
                          <th className="text-left py-2">Valor</th>
                          <th className="text-right py-2">Qtde</th>
                          <th className="text-right py-2">Ativo</th>
                          <th className="text-right py-2">Passivo</th>
                          <th className="text-right py-2">Valor causa Autor (Ativo)</th>
                          <th className="text-right py-2">Valor causa Réu (Passivo)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.data.map((r, i) => (
                          <tr key={r.key ?? i} className="border-b border-gray-100 dark:border-gray-700">
                            <td className="py-2">{r.label}</td>
                            <td className="text-right py-2">{r.count}</td>
                            <td className="text-right py-2">{r.countAutor}</td>
                            <td className="text-right py-2">{r.countReu}</td>
                            <td className="text-right py-2">{r.caseValueAutor}</td>
                            <td className="text-right py-2">{r.caseValueReu}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
            )}
            {archivedSnapshots && <ProveitoBlock data={archivedSnapshots} />}
          </div>
        )}

        {!loading && mode === 'member' && memberId && memberMetrics && (
          <div className="mt-8 space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                ['Prazos abertos', memberMetrics.open.deadlinesOpen],
                ['Tarefas abertas', memberMetrics.open.tasksOpen],
                ['Audiências abertas', memberMetrics.open.hearingsOpen],
                ['Eventos abertos', memberMetrics.open.eventsOpen],
                ['Prazos realizados', memberMetrics.done.deadlinesDone],
                ['Tarefas realizadas', memberMetrics.done.tasksDone],
                ['Horas em execução (Tarefa)', memberMetrics.hoursByTaskType.find((h) => h.taskType === 'TASK')?.hoursInProgress ?? 0],
                ['Horas bloqueadas (Tarefa)', memberMetrics.hoursByTaskType.find((h) => h.taskType === 'TASK')?.hoursBlocked ?? 0],
                ['Horas suspensas (Tarefa)', memberMetrics.hoursByTaskType.find((h) => h.taskType === 'TASK')?.hoursOnHold ?? 0],
                ['Horas em execução (Prazo)', memberMetrics.hoursByTaskType.find((h) => h.taskType === 'DEADLINE')?.hoursInProgress ?? 0],
                ['Horas bloqueadas (Prazo)', memberMetrics.hoursByTaskType.find((h) => h.taskType === 'DEADLINE')?.hoursBlocked ?? 0],
                ['Horas suspensas (Prazo)', memberMetrics.hoursByTaskType.find((h) => h.taskType === 'DEADLINE')?.hoursOnHold ?? 0],
                ['Audiências realizadas', memberMetrics.done.hearingsDone],
                ['Eventos realizados', memberMetrics.done.eventsDone],
                ['Itens em atraso', memberMetrics.overdue.totalOverdue],
                ['Média dias atraso', memberMetrics.overdue.averageDaysOverdue],
                ['Nível', memberMetrics.level],
                ['Sequência', memberMetrics.streak],
              ].map(([label, value]) => (
                <div key={String(label)} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-[15px]" style={cardStyle}>
                  <p className="text-[11px] font-semibold text-[#2D3748] dark:text-gray-300 uppercase mb-1">{label}</p>
                  <p className="text-lg font-semibold text-[#2D3748] dark:text-gray-100">{String(value)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && mode === 'client' && !clientId && (
          <div className="mt-8 text-center text-gray-500 dark:text-gray-400 text-sm">Selecione um cliente.</div>
        )}
        {!loading && mode === 'member' && !memberId && (
          <div className="mt-8 text-center text-gray-500 dark:text-gray-400 text-sm">Selecione um membro.</div>
        )}
        </div>
      </div>
    </div>
  );
}
